import { getApiUrl, getAuthHeaders, getAuthHeadersForFileUpload } from '../config/api';

interface ApiResponse<T = any> {
  success: boolean;
  data: T;
  message: string;
}

class ApiService {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = getApiUrl(endpoint);
    const defaultHeaders = getAuthHeaders();
    
    const config: RequestInit = {
      ...options,
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'Origin': window.location.origin,
        ...defaultHeaders,
        ...options.headers,
      },
      cache: 'no-cache',
      mode: 'cors',
      credentials: 'include',
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired or invalid
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        
        // Handle different error types
        const errorMessage = data.message || data.error || `Request failed with status ${response.status}`;
        const error = new Error(errorMessage);
        (error as any).status = response.status;
        (error as any).data = data;
        throw error;
      }

      return data;
    } catch (error) {
      console.error('API Request failed:', error);
      throw error;
    }
  }

  // GET request
  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  // POST request
  async post<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // PUT request
  async put<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // PATCH request
  async patch<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // DELETE request
  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  // File upload request
  async uploadFile<T>(endpoint: string, formData: FormData): Promise<ApiResponse<T>> {
    const url = getApiUrl(endpoint);
    const headers = getAuthHeadersForFileUpload();

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Upload failed');
      }

      return data;
    } catch (error) {
      console.error('File upload failed:', error);
      throw error;
    }
  }

  // Auth specific methods
  async login(email: string, password: string): Promise<ApiResponse> {
    return this.post('/api/auth/login', { email, password });
  }

  async register(userData: any): Promise<ApiResponse> {
    return this.post('/api/auth/register', userData);
  }

  async logout(): Promise<ApiResponse> {
    return this.post('/api/auth/logout');
  }

  async getCurrentUser(): Promise<ApiResponse> {
    return this.get('/api/auth/me');
  }

  // User management methods
  async getUsers(params?: any): Promise<ApiResponse> {
    const queryString = params ? new URLSearchParams(params).toString() : '';
    return this.get(`/api/users${queryString ? `?${queryString}` : ''}`);
  }

  async getUser(id: number): Promise<ApiResponse> {
    return this.get(`/api/users/${id}`);
  }

  async createUser(userData: any): Promise<ApiResponse> {
    return this.post('/api/users', userData);
  }

  async updateUser(id: number, userData: any): Promise<ApiResponse> {
    return this.put(`/api/users/${id}`, userData);
  }

  async deleteUser(id: number): Promise<ApiResponse> {
    return this.delete(`/api/users/${id}`);
  }

  async updateUserStatus(id: number, status: string): Promise<ApiResponse> {
    return this.patch(`/api/users/${id}/status`, { status });
  }

  // Role management methods
  async getRoles(): Promise<ApiResponse> {
    return this.get('/api/roles');
  }

  async getRole(id: number): Promise<ApiResponse> {
    return this.get(`/api/roles/${id}`);
  }

  async createRole(roleData: any): Promise<ApiResponse> {
    return this.post('/api/roles', roleData);
  }

  async updateRole(id: number, roleData: any): Promise<ApiResponse> {
    return this.put(`/api/roles/${id}`, roleData);
  }

  async deleteRole(id: number): Promise<ApiResponse> {
    return this.delete(`/api/roles/${id}`);
  }

  // Module methods
  async getModules(): Promise<ApiResponse<Module[]>> {
    return this.get('/api/modules');
  }

  async getModule(id: number): Promise<ApiResponse<Module>> {
    return this.get(`/api/modules/${id}`);
  }

  async createModule(moduleData: any): Promise<ApiResponse<Module>> {
    return this.post('/api/modules', moduleData);
  }

  async updateModule(id: number, moduleData: any): Promise<ApiResponse<Module>> {
    return this.put(`/api/modules/${id}`, moduleData);
  }

  async deleteModule(id: number): Promise<ApiResponse<void>> {
    return this.delete(`/api/modules/${id}`);
  }

  // Permission management methods
  async getPermissions(): Promise<ApiResponse> {
    return this.get('/api/permissions');
  }

  async getPermissionsByModule(module: string): Promise<ApiResponse> {
    return this.get(`/api/permissions/module/${module}`);
  }

  async getPermissionModules(): Promise<ApiResponse> {
    return this.get('/api/permissions/modules');
  }

  async validateEmail(email: string, userId?: number): Promise<ApiResponse> {
    const data: any = { email };
    if (userId) {
      data.user_id = userId;
    }
    return this.post('/api/users/validate-email', data);
  }

  // Customer management
  async getCustomers(): Promise<ApiResponse<Customer[]>> {
    return this.get('/api/customers');
  }

  async getCustomer(id: number): Promise<ApiResponse<Customer>> {
    return this.get(`/api/customers/${id}`);
  }

  async createCustomer(customerData: any): Promise<ApiResponse<Customer>> {
    return this.post('/api/customers', customerData);
  }

  async updateCustomer(id: number, customerData: any): Promise<ApiResponse<Customer>> {
    return this.put(`/api/customers/${id}`, customerData);
  }

  async deleteCustomer(id: number): Promise<ApiResponse<void>> {
    return this.delete(`/api/customers/${id}`);
  }

  // Customer location management
  async getCustomerLocations(customerId?: number): Promise<ApiResponse<CustomerLocation[]>> {
    const endpoint = customerId 
      ? `/api/customer-locations?customer_id=${customerId}`
      : '/api/customer-locations';
    return this.get(endpoint);
  }

  async createCustomerLocation(data: { customer_id: number; location_id: number; address: string; sort_order?: number }): Promise<ApiResponse<CustomerLocation>> {
    return this.post('/api/customer-locations', data);
  }

  async updateCustomerLocation(id: number, data: { customer_id: number; location_id: number; address: string; sort_order?: number }): Promise<ApiResponse<CustomerLocation>> {
    return this.put(`/api/customer-locations/${id}`, data);
  }

  async deleteCustomerLocation(id: number): Promise<ApiResponse<void>> {
    return this.delete(`/api/customer-locations/${id}`);
  }

  async bulkSaveCustomerLocations(customerId: number, locations: Array<{ location_id: number; address: string }>): Promise<ApiResponse<CustomerLocation[]>> {
    return this.post('/api/customer-locations/bulk', { customer_id: customerId, locations });
  }

  // Location management
  async getLocations(): Promise<ApiResponse<Location[]>> {
    return this.get('/api/locations');
  }

  async getLocation(id: number): Promise<ApiResponse<Location>> {
    return this.get(`/api/locations/${id}`);
  }

  async createLocation(locationData: any): Promise<ApiResponse<Location>> {
    return this.post('/api/locations', locationData);
  }

  async updateLocation(id: number, locationData: any): Promise<ApiResponse<Location>> {
    return this.put(`/api/locations/${id}`, locationData);
  }

  async deleteLocation(id: number): Promise<ApiResponse<void>> {
    return this.delete(`/api/locations/${id}`);
  }

  // UAE Emirates and Cities
  async getUaeEmirates(): Promise<ApiResponse<UaeEmirate[]>> {
    return this.get('/api/uae/emirates');
  }

  async getUaeCities(): Promise<ApiResponse<UaeCity[]>> {
    return this.get('/api/uae/cities');
  }

  async getCitiesByEmirate(emirateId: number): Promise<ApiResponse<UaeCity[]>> {
    return this.get(`/api/uae/emirates/${emirateId}/cities`);
  }

  // Team management
  async getTeams(): Promise<ApiResponse<Team[]>> {
    return this.get('/api/teams');
  }

  async getTeam(id: number): Promise<ApiResponse<Team>> {
    return this.get(`/api/teams/${id}`);
  }

  async createTeam(teamData: any): Promise<ApiResponse<Team>> {
    return this.post('/api/teams', teamData);
  }

  async updateTeam(id: number, teamData: any): Promise<ApiResponse<Team>> {
    return this.put(`/api/teams/${id}`, teamData);
  }

  async deleteTeam(id: number): Promise<ApiResponse<void>> {
    return this.delete(`/api/teams/${id}`);
  }

  async getTeamsDropdownData(): Promise<ApiResponse<any>> {
    return this.get('/api/teams/dropdown-data');
  }

  // Subscription Type management methods
  async getSubscriptionTypes(): Promise<ApiResponse<any[]>> {
    return this.get('/api/subscription-types');
  }

  async getSubscriptionType(id: number): Promise<ApiResponse<any>> {
    return this.get(`/api/subscription-types/${id}`);
  }

  async createSubscriptionType(subscriptionTypeData: any): Promise<ApiResponse<any>> {
    return this.post('/api/subscription-types', subscriptionTypeData);
  }

  async updateSubscriptionType(id: number, subscriptionTypeData: any): Promise<ApiResponse<any>> {
    return this.put(`/api/subscription-types/${id}`, subscriptionTypeData);
  }

  async deleteSubscriptionType(id: number): Promise<ApiResponse<void>> {
    return this.delete(`/api/subscription-types/${id}`);
  }

  // Frequency management methods
  async getFrequencies(): Promise<ApiResponse<any[]>> {
    return this.get('/api/frequencies');
  }

  async getFrequency(id: number): Promise<ApiResponse<any>> {
    return this.get(`/api/frequencies/${id}`);
  }

  async createFrequency(frequencyData: any): Promise<ApiResponse<any>> {
    return this.post('/api/frequencies', frequencyData);
  }

  async updateFrequency(id: number, frequencyData: any): Promise<ApiResponse<any>> {
    return this.put(`/api/frequencies/${id}`, frequencyData);
  }

  async deleteFrequency(id: number): Promise<ApiResponse<void>> {
    return this.delete(`/api/frequencies/${id}`);
  }

  // Subscription management methods
  async getSubscriptions(): Promise<ApiResponse<any[]>> {
    return this.get('/api/subscriptions');
  }

  async getSubscription(id: number): Promise<ApiResponse<any>> {
    return this.get(`/api/subscriptions/${id}`);
  }

  async createSubscription(subscriptionData: any): Promise<ApiResponse<any>> {
    return this.post('/api/subscriptions', subscriptionData);
  }

  async updateSubscription(id: number, subscriptionData: any): Promise<ApiResponse<any>> {
    return this.put(`/api/subscriptions/${id}`, subscriptionData);
  }

  async deleteSubscription(id: number): Promise<ApiResponse<void>> {
    return this.delete(`/api/subscriptions/${id}`);
  }

  // Customer subscription management methods
  async getAvailableSubscriptions(): Promise<ApiResponse<any[]>> {
    return this.get('/api/available-subscriptions');
  }

  async getCustomerWithSubscriptions(customerId: number): Promise<ApiResponse<Customer>> {
    return this.get(`/api/customers/${customerId}/subscriptions`);
  }

  async assignSubscriptionsToCustomer(customerId: number, subscriptions: any[]): Promise<ApiResponse<Customer>> {
    return this.post(`/api/customers/${customerId}/subscriptions`, { subscriptions });
  }

  // Supplier management methods
  async getSuppliers(): Promise<ApiResponse<Supplier[]>> {
    return this.get('/api/suppliers');
  }

  async getSupplier(id: number): Promise<ApiResponse<Supplier>> {
    return this.get(`/api/suppliers/${id}`);
  }

  async createSupplier(supplierData: any): Promise<ApiResponse<Supplier>> {
    return this.post('/api/suppliers', supplierData);
  }

  async updateSupplier(id: number, supplierData: any): Promise<ApiResponse<Supplier>> {
    return this.put(`/api/suppliers/${id}`, supplierData);
  }

  async deleteSupplier(id: number): Promise<ApiResponse<void>> {
    return this.delete(`/api/suppliers/${id}`);
  }

  async getSupplierDropdownData(): Promise<ApiResponse<any>> {
    return this.get('/api/suppliers/dropdown-data');
  }

  // Supplier Type management methods
  async getSupplierTypes(): Promise<ApiResponse<any[]>> {
    return this.get('/api/supplier-types');
  }

  async getSupplierType(id: number): Promise<ApiResponse<any>> {
    return this.get(`/api/supplier-types/${id}`);
  }

  async createSupplierType(supplierTypeData: any): Promise<ApiResponse<any>> {
    return this.post('/api/supplier-types', supplierTypeData);
  }

  async updateSupplierType(id: number, supplierTypeData: any): Promise<ApiResponse<any>> {
    return this.put(`/api/supplier-types/${id}`, supplierTypeData);
  }

  async deleteSupplierType(id: number): Promise<ApiResponse<void>> {
    return this.delete(`/api/supplier-types/${id}`);
  }

  // Payment Type management methods
  async getPaymentTypes(): Promise<ApiResponse<any[]>> {
    return this.get('/api/payment-types');
  }

  async getPaymentType(id: number): Promise<ApiResponse<any>> {
    return this.get(`/api/payment-types/${id}`);
  }

  async createPaymentType(paymentTypeData: any): Promise<ApiResponse<any>> {
    return this.post('/api/payment-types', paymentTypeData);
  }

  async updatePaymentType(id: number, paymentTypeData: any): Promise<ApiResponse<any>> {
    return this.put(`/api/payment-types/${id}`, paymentTypeData);
  }

  async deletePaymentType(id: number): Promise<ApiResponse<void>> {
    return this.delete(`/api/payment-types/${id}`);
  }

  // Item management methods
  async getItems(): Promise<ApiResponse<Item[]>> {
    return this.get('/api/items');
  }

  async getItem(id: number): Promise<ApiResponse<Item>> {
    return this.get(`/api/items/${id}`);
  }

  async createItem(itemData: any): Promise<ApiResponse<Item>> {
    return this.post('/api/items', itemData);
  }

  async updateItem(id: number, itemData: any): Promise<ApiResponse<Item>> {
    return this.put(`/api/items/${id}`, itemData);
  }

  async deleteItem(id: number): Promise<ApiResponse<void>> {
    return this.delete(`/api/items/${id}`);
  }

  // Stock management methods
  async getStocks(): Promise<ApiResponse<Stock[]>> {
    return this.get('/api/stocks');
  }

  async getStocksBySupplier(supplierId: number): Promise<ApiResponse<Stock[]>> {
    return this.get(`/api/stocks/supplier/${supplierId}`);
  }

  async getStock(id: number): Promise<ApiResponse<Stock>> {
    return this.get(`/api/stocks/${id}`);
  }

  async createStock(stockData: any): Promise<ApiResponse<Stock>> {
    return this.post('/api/stocks', stockData);
  }

  async updateStock(id: number, stockData: any): Promise<ApiResponse<Stock>> {
    return this.put(`/api/stocks/${id}`, stockData);
  }

  async deleteStock(id: number): Promise<ApiResponse<void>> {
    return this.delete(`/api/stocks/${id}`);
  }

  async getStockDropdownData(): Promise<ApiResponse<any>> {
    return this.get('/api/stocks/dropdown-data');
  }

  // Category management
  async getCategories(): Promise<ApiResponse<Category[]>> {
    return this.get('/api/categories');
  }

  async getCategory(id: number): Promise<ApiResponse<Category>> {
    return this.get(`/api/categories/${id}`);
  }

  async createCategory(data: Partial<Category>): Promise<ApiResponse<Category>> {
    return this.post('/api/categories', data);
  }

  async updateCategory(id: number, data: Partial<Category>): Promise<ApiResponse<Category>> {
    return this.put(`/api/categories/${id}`, data);
  }

  async deleteCategory(id: number): Promise<ApiResponse> {
    return this.delete(`/api/categories/${id}`);
  }

  // Orders
  async getOrders(): Promise<ApiResponse<Order[]>> {
    return this.get('/api/orders');
  }

  async getOrdersBySupplier(supplierId: number): Promise<ApiResponse<Order[]>> {
    return this.get(`/api/orders/supplier/${supplierId}`);
  }

  async getOrder(id: number): Promise<ApiResponse<Order>> {
    return this.get(`/api/orders/${id}`);
  }

  async createOrder(data: Partial<Order>): Promise<ApiResponse<Order>> {
    return this.post('/api/orders', data);
  }

  async updateOrder(id: number, data: Partial<Order>): Promise<ApiResponse<Order>> {
    return this.put(`/api/orders/${id}`, data);
  }

  async deleteOrder(id: number): Promise<ApiResponse> {
    return this.delete(`/api/orders/${id}`);
  }

  // Stock check
  async checkStockAvailability(itemName: string): Promise<ApiResponse<{ available_stock: number }>> {
    return this.get(`/api/stock/check/${encodeURIComponent(itemName)}`);
  }

  // Invoices
  async getInvoices(): Promise<ApiResponse<Invoice[]>> {
    return this.get('/api/invoices');
  }

  async getInvoice(id: number): Promise<ApiResponse<Invoice>> {
    return this.get(`/api/invoices/${id}`);
  }

  async createInvoice(data: Partial<Invoice>): Promise<ApiResponse<Invoice>> {
    return this.post('/api/invoices', data);
  }

  async updateInvoice(id: number, data: Partial<Invoice>): Promise<ApiResponse<Invoice>> {
    return this.put(`/api/invoices/${id}`, data);
  }

  async deleteInvoice(id: number): Promise<ApiResponse> {
    return this.delete(`/api/invoices/${id}`);
  }

  async getInvoiceDropdownData(): Promise<ApiResponse<any>> {
    return this.get('/api/invoices/dropdown-data');
  }

  // Call Center API methods
  async getCallCenterAgents(): Promise<ApiResponse<CallCenterAgent[]>> {
    return this.get('/api/call-center/agents');
  }

  async getCallCenterAgent(id: number): Promise<ApiResponse<CallCenterAgent>> {
    return this.get(`/api/call-center/agents/${id}`);
  }

  async createCallCenterAgent(data: Partial<CallCenterAgent>): Promise<ApiResponse<CallCenterAgent>> {
    return this.post('/api/call-center/agents', data);
  }

  async updateCallCenterAgent(id: number, data: Partial<CallCenterAgent>): Promise<ApiResponse<CallCenterAgent>> {
    return this.put(`/api/call-center/agents/${id}`, data);
  }

  async deleteCallCenterAgent(id: number): Promise<ApiResponse<void>> {
    return this.delete(`/api/call-center/agents/${id}`);
  }

  async getCallRecords(customerId?: number): Promise<ApiResponse<CallRecord[]>> {
    const endpoint = customerId 
      ? `/api/call-center/records?customer_id=${customerId}`
      : '/api/call-center/records';
    return this.get(endpoint);
  }

  async getCallRecord(id: number): Promise<ApiResponse<CallRecord>> {
    return this.get(`/api/call-center/records/${id}`);
  }

  async createCallRecord(data: Partial<CallRecord>): Promise<ApiResponse<CallRecord>> {
    return this.post('/api/call-center/records', data);
  }

  async updateCallRecord(id: number, data: Partial<CallRecord>): Promise<ApiResponse<CallRecord>> {
    return this.put(`/api/call-center/records/${id}`, data);
  }

  async deleteCallRecord(id: number): Promise<ApiResponse<void>> {
    return this.delete(`/api/call-center/records/${id}`);
  }

  async getCallCenterDropdownData(): Promise<ApiResponse<any>> {
    return this.get('/api/call-center/dropdown-data');
  }
}

interface Module {
  id: number;
  name: string;
  display_name: string;
  description: string;
  icon: string;
  route: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
}

export interface Customer {
  id: number;
  name: string;
  email: string;
  phone: string;
  company_name?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  contact_person?: string;
  status: 'active' | 'inactive' | 'suspended';
  credit_limit?: number;
  current_balance?: number;
  notes?: string;
  subscriptions?: CustomerSubscription[];
  created_at: string;
  updated_at: string;
}

export interface CustomerSubscription {
  id: number;
  name: string;
  subscription_number: string;
  price: number;
  status: 'active' | 'inactive' | 'suspended' | 'expired';
  start_date: string;
  end_date?: string;
  amount_paid: number;
  notes?: string;
  category?: {
    id: number;
    name: string;
    display_name: string;
  };
  subscription_type?: {
    id: number;
    name: string;
    display_name: string;
  };
  frequency?: {
    id: number;
    name: string;
    display_name: string;
  };
}

export interface Location {
  id: number;
  name: string;
  address?: string;
  city?: string;
  emirate_id?: number;
  city_id?: number;
  area?: string;
  landmark_1?: string;
  landmark_2?: string;
  remarks?: string;
  status: 'active' | 'inactive';
  notes?: string;
  created_at: string;
  updated_at: string;
  emirate?: UaeEmirate;
  cityRelation?: UaeCity;
}

export interface CustomerLocation {
  id: number;
  customer_id: number;
  location_id: number;
  address: string;
  sort_order: number;
  location?: {
    id: number;
    name: string;
    city?: string;
  };
  created_at: string;
  updated_at: string;
}

export interface TeamMember {
  id: number;
  team_id: number;
  user_id: number;
  location_id: number;
  user?: {
    id: number;
    name: string;
    email: string;
  };
  location?: {
    id: number;
    name: string;
    city?: string;
  };
}

export interface Team {
  id: number;
  name: string;
  supervisor_id: number;
  supervisor?: {
    id: number;
    name: string;
    email: string;
  };
  status: 'active' | 'inactive';
  notes?: string;
  members?: TeamMember[];
  created_at: string;
  updated_at: string;
}

export interface Supplier {
  id: number;
  name: string;
  supplier_name: string;
  contact_person?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  website?: string;
  supplier_type?: {
    id: number;
    name: string;
    display_name: string;
  };
  payment_type?: {
    id: number;
    name: string;
    display_name: string;
  };
  status: 'active' | 'inactive' | 'suspended';
  notes?: string;
  current_stock?: number;
  created_at: string;
  updated_at: string;
}

export interface Item {
  id: number;
  name: string;
  item_code: string;
  description?: string;
  category_id?: number;
  category?: {
    id: number;
    name: string;
    color: string;
  };
  unit_price: number | string;
  unit: string;
  reorder_level: number;
  status: 'active' | 'inactive';
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface Order {
  id: number;
  teckit_no?: string;
  customer_id?: number;
  customer?: {
    id: number;
    company_name: string;
    contact_person: string;
    email: string;
    phone: string;
  };
  item_name: string;
  campaign_name?: string;
  voucher_name?: string;
  voucher_number?: string;
  period?: string;
  unit_price?: number;
  quantity?: number;
  created_by?: string;
  creation_date?: string;
  coordinated_by?: string;
  coordinated_date?: string;
  feedback_by?: string;
  feedback_date?: string;
  effective_start_date?: string;
  effective_end_date?: string;
  followed_by?: string;
  follow_status?: string;
  collection_date?: string;
  collection_time?: string;
  notes?: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  assigned_employees?: number[];
  created_at: string;
  updated_at: string;
}

export interface Invoice {
  id: number;
  invoice_number: string;
  customer_id?: number;
  customer?: {
    id: number;
    company_name: string;
    contact_person: string;
    email: string;
    phone: string;
  };
  invoice_date: string;
  due_date?: string;
  subtotal: number;
  tax_amount: number;
  discount_amount: number;
  total_amount: number;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  notes?: string;
  terms_conditions?: string;
  payment_method?: string;
  payment_date?: string;
  billing_address?: string;
  shipping_address?: string;
  created_at: string;
  updated_at: string;
}

export interface Stock {
  id: number;
  item_id: number;
  supplier_id?: number;
  quantity: number;
  unit_cost: number;
  stock_date: string;
  transaction_type: 'in' | 'out';
  reference_number?: string;
  notes?: string;
  item?: {
    id: number;
    name: string;
    item_code: string;
    unit: string;
  };
  supplier?: {
    id: number;
    name: string;
    supplier_name: string;
  };
  created_at: string;
  updated_at: string;
}

export interface Category {
  id: number;
  name: string;
  display_name?: string;
  description?: string;
  color?: string;
  status: 'active' | 'inactive';
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface CallCenterAgent {
  id: number;
  name: string;
  email: string;
  phone: string;
  department: string;
  status: 'active' | 'inactive' | 'busy';
  calls_handled: number;
  average_rating: number;
  join_date: string;
  created_at: string;
  updated_at: string;
}

export interface CallRecord {
  id: number;
  agent_id: number;
  agent_name: string;
  customer_id: number;
  customer_name: string;
  supplier_id?: number;
  supplier_name?: string;
  call_type: 'inbound' | 'outbound';
  duration: string;
  status: 'completed' | 'missed' | 'ongoing';
  rating: number;
  timestamp: string;
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface UaeEmirate {
  id: number;
  name: string;
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
}

export interface UaeCity {
  id: number;
  emirate_id: number;
  name: string;
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
  emirate?: UaeEmirate;
}

// Create and export a singleton instance
export const apiService = new ApiService();
export default apiService;
